import { useEffect, useMemo, useState } from "react";
import { Search, Wand2 } from "lucide-react";
import {
    Bot,
    CheckCircle2,
    PlayCircle,
    RefreshCw,
    Save,
    Trash2,
    XCircle
} from "lucide-react";
import toast from "react-hot-toast";
import {
    getAiProviderRoutes,
    saveAiProviderRoute,
    deleteAiProviderRoute,
    testAiProviderRoute,
    getAiProviderDiscoveryCatalog
} from "../services/crmService";
import "./AdminAiProviderRoutesPage.css";
import PageShell from "../components/layout/PageShell";

const blankForm = {
    id: "",
    providerKey: "",
    providerName: "",
    providerType: "OpenAiCompatible",
    modality: "text",
    featureKey: "*",
    routingMode: "Balanced",
    isEnabled: true,
    priority: 100,
    model: "",
    baseUrl: "",
    apiKeySettingName: "",
    temperature: 0.7,
    maxTokens: 4000,
    timeoutSeconds: 60,
    extraJson: ""
};

export default function AdminAiProviderRoutesPage() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(blankForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingId, setTestingId] = useState("");
    const [testResult, setTestResult] = useState(null);

    const [discoveryOpen, setDiscoveryOpen] = useState(false);
    const [discoveryLoading, setDiscoveryLoading] = useState(false);
    const [discoveryItems, setDiscoveryItems] = useState([]);
    const [discoveryFilter, setDiscoveryFilter] = useState("all");
    const [discoverySearch, setDiscoverySearch] = useState("");

    const grouped = useMemo(() => {
        return items.reduce((acc, item) => {
            const key = `${item.modality || "text"} / ${item.routingMode || "*"} / ${item.featureKey || "*"}`;
            acc[key] ||= [];
            acc[key].push(item);
            return acc;
        }, {});
    }, [items]);

    const filteredDiscoveryItems = useMemo(() => {
        const search = discoverySearch.trim().toLowerCase();

        return discoveryItems.filter((item) => {
            const matchesGroup =
                discoveryFilter === "all" ||
                (item.group || "").toLowerCase() === discoveryFilter.toLowerCase() ||
                (item.costClass || "").toLowerCase().includes(discoveryFilter.toLowerCase()) ||
                (item.modality || "").toLowerCase() === discoveryFilter.toLowerCase();

            const haystack = [
                item.providerName,
                item.providerKey,
                item.providerType,
                item.model,
                item.bestFor,
                item.compatibility,
                item.notes,
                ...(item.tags || [])
            ]
                .join(" ")
                .toLowerCase();

            return matchesGroup && (!search || haystack.includes(search));
        });
    }, [discoveryItems, discoveryFilter, discoverySearch]);

    const load = async () => {
        try {
            setLoading(true);
            const result = await getAiProviderRoutes();
            setItems(result?.items || []);
        } catch (error) {
            console.error(error);
            toast.error("Could not load AI provider routes.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const edit = (item) => {
        setForm({
            id: item.id || "",
            providerKey: item.providerKey || "",
            providerName: item.providerName || "",
            providerType: item.providerType || "OpenAiCompatible",
            modality: item.modality || "text",
            featureKey: item.featureKey || "*",
            routingMode: item.routingMode || "Balanced",
            isEnabled: item.isEnabled !== false,
            priority: item.priority ?? 100,
            model: item.model || "",
            baseUrl: item.baseUrl || "",
            apiKeySettingName: item.apiKeySettingName || "",
            temperature: item.temperature ?? 0.7,
            maxTokens: item.maxTokens ?? 4000,
            timeoutSeconds: item.timeoutSeconds ?? 60,
            extraJson: item.extraJson || ""
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const reset = () => {
        setForm(blankForm);
        setTestResult(null);
    };

    const update = (key, value) => {
        setForm((current) => ({
            ...current,
            [key]: value
        }));
    };

    const save = async () => {
        const errors = validateRouteForm(form);

        if (errors.length > 0) {
            toast.error(errors[0]);
            setTestResult({
                success: false,
                provider: form.providerName || "Validation",
                model: form.model || "",
                errorMessage: errors.join("\n"),
                durationMs: 0
            });
            return;
        }

        try {
            setSaving(true);

            const payload = {
                ...form,
                id: form.id || null,
                providerKey: form.providerKey.trim(),
                providerName: form.providerName.trim(),
                providerType: form.providerType.trim(),
                modality: form.modality.trim(),
                featureKey: form.featureKey.trim() || "*",
                routingMode: form.routingMode.trim() || "Balanced",
                priority: Number(form.priority || 100),
                temperature: form.temperature === "" ? null : Number(form.temperature),
                maxTokens: form.maxTokens === "" ? null : Number(form.maxTokens),
                timeoutSeconds: form.timeoutSeconds === "" ? null : Number(form.timeoutSeconds)
            };

            const result = await saveAiProviderRoute(payload);

            toast.success("AI provider route saved.");
            setForm({
                ...blankForm,
                ...(result?.item || {})
            });

            await load();
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Could not save route.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete ${item.providerName} route?`)) return;

        try {
            await deleteAiProviderRoute(item.id);
            toast.success("Route deleted.");
            await load();

            if (form.id === item.id) reset();
        } catch (error) {
            console.error(error);
            toast.error("Could not delete route.");
        }
    };

    const test = async (item) => {
        try {
            setTestingId(item.id);
            setTestResult(null);

            const result = await testAiProviderRoute(
                item.id,
                "You are testing an AI route. Return one short sentence only.",
                "Say that Shriansh Nexus provider route is working."
            );

            setTestResult({
                ...result,
                routeModality: item.modality,
                routeProviderType: item.providerType
            });

            if (result?.success) {
                toast.success("Provider test successful.");
            } else {
                toast.error(result?.errorMessage || "Provider test failed.");
            }
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Provider test failed.");
        } finally {
            setTestingId("");
        }
    };

    const openDiscovery = async () => {
        setDiscoveryOpen(true);

        if (discoveryItems.length > 0) return;

        try {
            setDiscoveryLoading(true);
            const result = await getAiProviderDiscoveryCatalog();
            setDiscoveryItems(result?.items || []);
        } catch (error) {
            console.error(error);
            toast.error("Could not load provider discovery catalog.");
        } finally {
            setDiscoveryLoading(false);
        }
    };

    const createDraftFromDiscovery = (item) => {
        setForm({
            ...blankForm,
            id: "",
            providerKey: item.providerKey || "",
            providerName: item.providerName || "",
            providerType: item.providerType || "OpenAiCompatible",
            modality: item.modality || "text",
            featureKey: item.featureKey || "*",
            routingMode: item.routingMode || "Balanced",
            isEnabled: false,
            priority: item.priority ?? 100,
            model: item.model || "",
            baseUrl: item.baseUrl || "",
            apiKeySettingName: item.apiKeySettingName || "",
            temperature: item.temperature ?? "",
            maxTokens: item.maxTokens ?? "",
            timeoutSeconds: item.timeoutSeconds ?? "",
            extraJson: item.extraJson || ""
        });

        setDiscoveryOpen(false);
        setTestResult(null);

        toast.success("Route draft created. Review, test, then save.");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const applyProviderSuggestions = () => {
        const apiKeySettingName = getApiKeySuggestion(
            form.providerName,
            form.providerKey,
            form.providerType
        );

        const baseUrl = getBaseUrlSuggestion(form.providerName, form.providerType);
        const extraJson = getExtraJsonSuggestion(form.providerType, form.providerName);

        setForm((current) => ({
            ...current,
            apiKeySettingName: current.apiKeySettingName || apiKeySettingName,
            baseUrl: current.baseUrl || baseUrl,
            extraJson: current.extraJson || extraJson
        }));

        toast.success("Suggestions applied.");
    };

    const applyUseCasePreset = (preset) => {
        const patches = {
            websiteGeneration: {
                featureKey: "*",
                routingMode: "QualityFirst",
                priority: 20,
                temperature: 0.7,
                maxTokens: 5000
            },
            crmFast: {
                featureKey: "crm-module-generate",
                routingMode: "Balanced",
                priority: 20,
                temperature: 0.6,
                maxTokens: 4000
            },
            autofill: {
                featureKey: "website-autofill",
                routingMode: "Balanced",
                priority: 15,
                temperature: 0.5,
                maxTokens: 2500
            },
            repair: {
                featureKey: "repair",
                routingMode: "Balanced",
                priority: 30,
                temperature: 0.4,
                maxTokens: 4000
            },
            heroImage: {
                modality: "image",
                featureKey: "hero",
                routingMode: "Balanced",
                priority: 10,
                timeoutSeconds: 90
            },
            imageFallback: {
                modality: "image",
                featureKey: "*",
                routingMode: "Balanced",
                priority: 50,
                timeoutSeconds: 90
            }
        };

        setForm((current) => ({
            ...current,
            ...(patches[preset] || {})
        }));

        toast.success("Preset applied.");
    };

    const duplicateAsNew = () => {
        setForm((current) => ({
            ...current,
            id: "",
            providerKey: `${current.providerKey || "provider"}-copy`,
            isEnabled: false
        }));

        toast.success("Duplicated as disabled draft.");
    };

    return (
        <PageShell title="ai providers">
            <div className="ai-routes-page">
                <section className="ai-routes-hero">
                    <div>
                        <span>SuperAdmin AI control</span>
                        <h1>AI Provider Routes</h1>
                        <p>
                            Manage DB-driven AI provider routing, model selection, priority,
                            and live provider testing without redeploying code.
                        </p>
                    </div>

                    <div className="ai-routes-hero-actions">
                        <button type="button" onClick={openDiscovery}>
                            <Search size={16} />
                            Discover Providers
                        </button>

                        <button type="button" onClick={load} disabled={loading}>
                            <RefreshCw size={16} />
                            Refresh
                        </button>
                    </div>
                </section>

                <section className="ai-route-form-card">
                    <div className="ai-route-form-head">
                        <div>
                            <strong>{form.id ? "Edit provider route" : "Add provider route"}</strong>
                            <span>Secrets stay in Azure/AppSettings. Store only setting names here.</span>
                        </div>

                        <button type="button" onClick={reset}>
                            New Route
                        </button>
                    </div>

                    <div className="ai-route-wizard-strip">
                        <div>
                            <strong>Guided setup</strong>
                            <span>{getProviderHelp(form.providerType)}</span>
                        </div>

                        <div className="ai-route-wizard-actions">
                            <button type="button" onClick={openDiscovery}>
                                <Search size={15} />
                                Discover
                            </button>

                            <button type="button" onClick={applyProviderSuggestions}>
                                <Wand2 size={15} />
                                Apply Suggestions
                            </button>

                            {form.id ? (
                                <button type="button" onClick={duplicateAsNew}>
                                    Duplicate as Draft
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div className="ai-route-preset-row">
                        <button type="button" onClick={() => applyUseCasePreset("websiteGeneration")}>
                            Website Generation
                        </button>
                        <button type="button" onClick={() => applyUseCasePreset("crmFast")}>
                            CRM Fast Generate
                        </button>
                        <button type="button" onClick={() => applyUseCasePreset("autofill")}>
                            Autofill
                        </button>
                        <button type="button" onClick={() => applyUseCasePreset("repair")}>
                            Repair/Critic
                        </button>
                        <button type="button" onClick={() => applyUseCasePreset("heroImage")}>
                            Hero Image
                        </button>
                        <button type="button" onClick={() => applyUseCasePreset("imageFallback")}>
                            Image Fallback
                        </button>
                    </div>

                    <div className="ai-route-form-grid">
                        <TextInput label="Provider Key" value={form.providerKey} onChange={(v) => update("providerKey", v)} />
                        <TextInput label="Provider Name" value={form.providerName} onChange={(v) => update("providerName", v)} />

                        <SelectInput
                            label="Provider Type"
                            value={form.providerType}
                            onChange={(v) => update("providerType", v)}
                            options={[
                                "OpenAiCompatible",
                                "GeminiNative",
                                "AnthropicNative",
                                "StockImageSearch",
                                "OpenAiImageCompatible",
                                "StabilityMultipart",
                                "GeminiImageNative"
                            ]}
                        />

                        <SelectInput
                            label="Modality"
                            value={form.modality}
                            onChange={(v) => update("modality", v)}
                            options={["text", "image"]}
                        />

                        <TextInput label="Feature Key" value={form.featureKey} onChange={(v) => update("featureKey", v)} />

                        <SelectInput
                            label="Routing Mode"
                            value={form.routingMode}
                            onChange={(v) => update("routingMode", v)}
                            options={["*", "Balanced", "QualityFirst", "CostFirst", "LocalFirst"]}
                        />

                        <NumberInput label="Priority" value={form.priority} onChange={(v) => update("priority", v)} />
                        <TextInput label="Model" value={form.model} onChange={(v) => update("model", v)} />

                        <TextInput label="Base URL" value={form.baseUrl} onChange={(v) => update("baseUrl", v)} />
                        <TextInput label="API Key Setting Name" value={form.apiKeySettingName} onChange={(v) => update("apiKeySettingName", v)} />

                        <NumberInput label="Temperature" value={form.temperature} onChange={(v) => update("temperature", v)} />
                        <NumberInput label="Max Tokens" value={form.maxTokens} onChange={(v) => update("maxTokens", v)} />
                        <NumberInput label="Timeout Seconds" value={form.timeoutSeconds} onChange={(v) => update("timeoutSeconds", v)} />

                        <label className="ai-route-checkbox">
                            <input
                                type="checkbox"
                                checked={form.isEnabled}
                                onChange={(e) => update("isEnabled", e.target.checked)}
                            />
                            Enabled
                        </label>
                    </div>

                    <div className={`ai-route-health ${form.isEnabled ? "enabled" : "disabled"}`}>
                        <strong>{form.isEnabled ? "Route will be active after save" : "Route is a safe disabled draft"}</strong>
                        <span>
                            {form.providerName || "Provider"} • {form.providerType} • {form.modality} •{" "}
                            {form.routingMode || "Balanced"} • {form.featureKey || "*"}
                        </span>
                        <small>
                            Secret expected from: {form.apiKeySettingName || "not configured"}
                        </small>
                    </div>

                    <label className="ai-route-extra">
                        <span>Extra JSON optional</span>
                        <textarea
                            value={form.extraJson}
                            onChange={(e) => update("extraJson", e.target.value)}
                            placeholder='{"headers":{"X-Custom":"value"}}'
                        />
                    </label>

                    <div className="ai-route-form-actions">
                        <button type="button" className="primary" onClick={save} disabled={saving}>
                            <Save size={16} />
                            {saving ? "Saving..." : "Save Route"}
                        </button>
                    </div>
                </section>

                <section className="ai-route-list-card">
                    <div className="ai-route-list-head">
                        <strong>Configured routes</strong>
                        <span>{items.length} route(s)</span>
                    </div>

                    {loading ? <p className="ai-route-empty">Loading routes...</p> : null}

                    {!loading && Object.entries(grouped).map(([group, routes]) => (
                        <div className="ai-route-group" key={group}>
                            <h3>{group}</h3>

                            <div className="ai-route-list">
                                {routes.map((item) => (
                                    <article key={item.id} className="ai-route-item">
                                        <div className="ai-route-main">
                                            <span className="ai-route-icon">
                                                <Bot size={17} />
                                            </span>

                                            <div>
                                                <strong>{item.providerName}</strong>
                                                <small>
                                                    {item.providerType} • {item.model || "No model"} • Priority {item.priority}
                                                </small>
                                                <em>{item.baseUrl}</em>
                                            </div>
                                        </div>

                                        <div className={`ai-route-status ${item.isEnabled ? "enabled" : "disabled"}`}>
                                            {item.isEnabled ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                                            {item.isEnabled ? "Enabled" : "Disabled"}
                                        </div>

                                        <div className="ai-route-actions">
                                            <button type="button" onClick={() => edit(item)}>
                                                Edit
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => test(item)}
                                                disabled={testingId === item.id}
                                            >
                                                <PlayCircle size={15} />
                                                {testingId === item.id ? "Testing..." : "Test"}
                                            </button>

                                            <button type="button" className="danger" onClick={() => remove(item)}>
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    ))}

                    {!loading && items.length === 0 ? (
                        <p className="ai-route-empty">No AI provider routes found.</p>
                    ) : null}
                </section>

                {testResult ? (
                    <section className={`ai-route-test-card ${testResult.success ? "success" : "failed"}`}>
                        <strong>Latest test result</strong>
                        <span>
                            {testResult.provider} / {testResult.model} / {testResult.durationMs}ms
                        </span>

                        {testResult.success ? (
                            <TestResultContent result={testResult} />
                        ) : (
                            <p>{testResult.errorMessage || "Provider test failed."}</p>
                        )}
                    </section>
                ) : null}

                {discoveryOpen ? (
                    <ProviderDiscoveryModal
                        loading={discoveryLoading}
                        items={filteredDiscoveryItems}
                        filter={discoveryFilter}
                        search={discoverySearch}
                        onFilterChange={setDiscoveryFilter}
                        onSearchChange={setDiscoverySearch}
                        onClose={() => setDiscoveryOpen(false)}
                        onCreateDraft={createDraftFromDiscovery}
                    />
                ) : null}

            </div>
        </PageShell>
    );
}

function validateRouteForm(form) {
    const errors = [];

    if (!form.providerKey?.trim()) errors.push("Provider Key is required.");
    if (!form.providerName?.trim()) errors.push("Provider Name is required.");
    if (!form.providerType?.trim()) errors.push("Provider Type is required.");
    if (!form.modality?.trim()) errors.push("Modality is required.");
    if (!form.featureKey?.trim()) errors.push("Feature Key is required.");
    if (!form.routingMode?.trim()) errors.push("Routing Mode is required.");

    if (!form.model?.trim() && form.providerType !== "StockImageSearch") {
        errors.push("Model is required for AI generation providers.");
    }

    if (!form.baseUrl?.trim()) {
        errors.push("Base URL is required.");
    }

    if (!form.apiKeySettingName?.trim()) {
        errors.push("API Key Setting Name is required. Keep the secret in Azure/AppSettings.");
    }

    if (form.extraJson?.trim()) {
        try {
            JSON.parse(form.extraJson);
        } catch {
            errors.push("Extra JSON must be valid JSON.");
        }
    }

    const priority = Number(form.priority);
    if (!Number.isFinite(priority) || priority <= 0) {
        errors.push("Priority must be a positive number.");
    }

    if (form.temperature !== "" && form.temperature !== null && form.temperature !== undefined) {
        const temperature = Number(form.temperature);
        if (!Number.isFinite(temperature) || temperature < 0 || temperature > 2) {
            errors.push("Temperature must be between 0 and 2.");
        }
    }

    if (form.maxTokens !== "" && form.maxTokens !== null && form.maxTokens !== undefined) {
        const maxTokens = Number(form.maxTokens);
        if (!Number.isFinite(maxTokens) || maxTokens < 100 || maxTokens > 32000) {
            errors.push("Max Tokens should be between 100 and 32000.");
        }
    }

    return errors;
}

function getProviderHelp(providerType) {
    switch (providerType) {
        case "OpenAiCompatible":
            return "Use this for Groq, OpenRouter, Together, Fireworks, and other /chat/completions-compatible providers.";
        case "GeminiNative":
            return "Use this for Gemini text models through Google generateContent API.";
        case "AnthropicNative":
            return "Use this for Claude/Anthropic Messages API.";
        case "StockImageSearch":
            return "Use this for Pexels/Unsplash stock image search. Returns image URLs, not generated images.";
        case "OpenAiImageCompatible":
            return "Use this for OpenAI image generation-compatible APIs.";
        case "StabilityMultipart":
            return "Use this for Stability AI multipart image generation.";
        case "GeminiImageNative":
            return "Use this for Gemini native image generation.";
        default:
            return "Select the provider protocol. Secrets must remain in Azure/AppSettings.";
    }
}

function getApiKeySuggestion(providerName, providerKey, providerType) {
    const name = String(providerName || providerKey || "").toLowerCase();

    if (name.includes("groq")) return "AiProviders:Groq:ApiKey";
    if (name.includes("openrouter")) return "AiProviders:OpenRouter:ApiKey";
    if (name.includes("gemini")) return "AiProviders:Gemini:ApiKey";
    if (name.includes("anthropic") || name.includes("claude")) return "AiProviders:Anthropic:ApiKey";
    if (name.includes("together")) return "AiProviders:Together:ApiKey";
    if (name.includes("fireworks")) return "AiProviders:Fireworks:ApiKey";
    if (name.includes("pexels")) return "AiProviders:Pexels:ApiKey";
    if (name.includes("unsplash")) return "AiProviders:Unsplash:AccessKey";
    if (name.includes("stability")) return "AiProviders:Stability:ApiKey";
    if (name.includes("openai")) return "AiProviders:OpenAI:ApiKey";

    if (providerType === "StockImageSearch") return "AiProviders:ProviderName:ApiKey";
    return "AiProviders:ProviderName:ApiKey";
}

function getBaseUrlSuggestion(providerName, providerType) {
    const name = String(providerName || "").toLowerCase();

    if (providerType === "OpenAiCompatible") {
        if (name.includes("groq")) return "https://api.groq.com/openai/v1/chat/completions";
        if (name.includes("openrouter")) return "https://openrouter.ai/api/v1/chat/completions";
        if (name.includes("together")) return "https://api.together.xyz/v1/chat/completions";
        if (name.includes("fireworks")) return "https://api.fireworks.ai/inference/v1/chat/completions";
        return "https://api.provider.com/v1/chat/completions";
    }

    if (providerType === "GeminiNative" || providerType === "GeminiImageNative") {
        return "https://generativelanguage.googleapis.com/v1beta";
    }

    if (providerType === "AnthropicNative") {
        return "https://api.anthropic.com/v1/messages";
    }

    if (providerType === "StockImageSearch") {
        if (name.includes("pexels")) return "https://api.pexels.com/v1/search";
        if (name.includes("unsplash")) return "https://api.unsplash.com/search/photos";
    }

    if (providerType === "OpenAiImageCompatible") {
        return "https://api.openai.com/v1/images/generations";
    }

    if (providerType === "StabilityMultipart") {
        return "https://api.stability.ai/v2beta/stable-image/generate/ultra";
    }

    return "";
}

function getExtraJsonSuggestion(providerType, providerName) {
    const name = String(providerName || "").toLowerCase();

    if (providerType === "AnthropicNative") {
        return "{\"anthropicVersion\":\"2023-06-01\"}";
    }

    if (providerType === "StockImageSearch") {
        if (name.includes("unsplash")) {
            return "{\"perPage\":\"1\",\"orientation\":\"landscape\",\"authScheme\":\"Client-ID\"}";
        }

        return "{\"perPage\":\"1\",\"orientation\":\"landscape\"}";
    }

    if (providerType === "OpenAiImageCompatible") {
        return "{\"size\":\"1024x1024\"}";
    }

    if (providerType === "StabilityMultipart") {
        return "{\"outputFormat\":\"png\",\"aspectRatio\":\"16:9\",\"negativePrompt\":\"text, watermark, logo, distorted hands, distorted face, blurry, low quality\"}";
    }

    return "{}";
}

function TextInput({ label, value, onChange }) {
    return (
        <label>
            <span>{label}</span>
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}

function NumberInput({ label, value, onChange }) {
    return (
        <label>
            <span>{label}</span>
            <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}

function SelectInput({ label, value, onChange, options }) {
    return (
        <label>
            <span>{label}</span>
            <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </label>
    );
}
function TestResultContent({ result }) {
    const content = result?.content || "";
    const isImage =
        result?.routeModality === "image" ||
        content.startsWith("http://") ||
        content.startsWith("https://") ||
        content.startsWith("data:image/") ||
        isLikelyBase64Image(content);

    if (!isImage) {
        return <p>{content}</p>;
    }

    const src = content.startsWith("http://") ||
        content.startsWith("https://") ||
        content.startsWith("data:image/")
        ? content
        : `data:image/png;base64,${content}`;

    return (
        <div className="ai-route-image-preview">
            <img src={src} alt="AI provider test result" />
            <div className="ai-route-image-actions">
                <a href={src} target="_blank" rel="noreferrer">
                    Open image →
                </a>
                <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(src)}
                >
                    Copy image URL/base64
                </button>
            </div>
        </div>
    );
}

function isLikelyBase64Image(value = "") {
    if (!value || value.length < 200) return false;
    return /^[A-Za-z0-9+/=]+$/.test(value);
}

function ProviderDiscoveryModal({
    loading,
    items,
    filter,
    search,
    onFilterChange,
    onSearchChange,
    onClose,
    onCreateDraft
}) {
    const filters = [
        { key: "all", label: "All" },
        { key: "Free / Trial Friendly", label: "Free / Trial" },
        { key: "Low Cost", label: "Low Cost" },
        { key: "Premium", label: "Premium" },
        { key: "image", label: "Images" },
        { key: "text", label: "Text" }
    ];

    return (
        <div className="ai-discovery-overlay">
            <div className="ai-discovery-modal">
                <div className="ai-discovery-head">
                    <div>
                        <span>Provider Discovery</span>
                        <strong>Recommended AI providers & models</strong>
                        <p>
                            Create disabled route drafts from curated provider templates.
                            Test and enable only after API keys and billing are ready.
                        </p>
                    </div>

                    <button type="button" onClick={onClose}>
                        Close
                    </button>
                </div>

                <div className="ai-discovery-controls">
                    <div className="ai-discovery-filters">
                        {filters.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={filter === item.key ? "active" : ""}
                                onClick={() => onFilterChange(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <input
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search provider, model, use-case..."
                    />
                </div>

                {loading ? (
                    <p className="ai-route-empty">Loading discovery catalog...</p>
                ) : null}

                {!loading && items.length === 0 ? (
                    <p className="ai-route-empty">No provider templates found.</p>
                ) : null}

                <div className="ai-discovery-grid">
                    {items.map((item) => (
                        <article key={`${item.providerKey}-${item.modality}-${item.routingMode}`} className="ai-discovery-card">
                            <div className="ai-discovery-card-head">
                                <div>
                                    <span>{item.group}</span>
                                    <strong>{item.providerName}</strong>
                                    <small>{item.providerType} • {item.modality}</small>
                                </div>

                                {item.isRecommended ? <em>Recommended</em> : null}
                            </div>

                            <div className="ai-discovery-model">
                                <b>{item.model || "No model"}</b>
                                <small>{item.costClass}</small>
                            </div>

                            <p>{item.bestFor}</p>

                            <ul>
                                <li>Feature: {item.featureKey}</li>
                                <li>Mode: {item.routingMode}</li>
                                <li>Priority: {item.priority}</li>
                                <li>Key setting: {item.apiKeySettingName || "-"}</li>
                            </ul>

                            <div className="ai-discovery-tags">
                                {(item.tags || []).slice(0, 5).map((tag) => (
                                    <span key={tag}>{tag}</span>
                                ))}
                            </div>

                            <small className="ai-discovery-note">{item.notes}</small>

                            <button type="button" onClick={() => onCreateDraft(item)}>
                                <Wand2 size={15} />
                                Create Route Draft
                            </button>
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
}